import React, { useState } from 'react';

import { AddToCalendarButton, CardContainer, FutureActionsWrapper } from './AppointmentCard.styled';
import { generateCalendarLink } from '../../utilities/appointmentProduct';
import { Button } from 'components/v2/Buttons/Button/Button';
import { LinkButton } from 'components/v2/Buttons/LinkButton/LinkButton';
import { Appointment, AppointmentType, Patient, utils } from 'kb-shared';
import { getAppointmentActionAvailability } from 'kb-shared/utilities/appointmentCancellation';
import { ModalCancelReschedule } from 'screens/ModalCancelResched/ModalCancelResched';
import getAppointmentChannel from 'utilities/getAppointmentChannel';

import { AppointmentDetails } from './AppointmentDetails';
import { LocationDetails } from './LocationDetails';
import { HiddenPatientIdTag, HiddenAppointmentTags } from './MarketingTags';
import { ProviderDetailsConfirmation, ProviderDetailsRegular } from './ProviderDetails';

const { inFuture, shouldDisplayProvider } = utils;

export const VIOS_CHICAGO_LAB_ID = 34;

type Props = {
  type: AppointmentType;
  appointment: Appointment;
  timeZone?: string;
  patient?: Patient;
  onReschedule?: VoidFunction;
  onAppointmentCancelled?: VoidFunction;
  confirmation?: boolean;
};

export const AppointmentCard = ({
  type,
  appointment,
  patient,
  onReschedule,
  onAppointmentCancelled,
  confirmation
}: Props) => {
  const isFuture = inFuture(appointment.endTime);
  const { isPhoneCall, isVideoCall, isInPerson } = getAppointmentChannel(type);
  const apptVideoLink = appointment.videoLink ?? type.videoLink;
  const urlParams = new URLSearchParams(window.location.search);
  const urlApptId = urlParams.get('apptid');
  const [showModal, setShowModal] = useState<boolean>(false || appointment?.id == urlApptId);
  const showProvider = Boolean(shouldDisplayProvider(appointment) && appointment.provider);
  const displayFullProviderDetails = showProvider && confirmation;
  const displayOnlyProviderName = showProvider && !confirmation;
  const { canCancelAppointment, canRescheduleAppointment, canModifyAppointment } =
    getAppointmentActionAvailability(appointment);
  const modifyAppointmentText =
    canCancelAppointment && canRescheduleAppointment
      ? 'Cancel or Reschedule'
      : canCancelAppointment
        ? 'Cancel Appointment'
        : 'Reschedule Appointment';

  const toggleCancelModal = () => {
    setShowModal(!showModal);
  };

  const goToVideoLink = () => {
    if (!apptVideoLink) return;
    window.location.href = apptVideoLink;
  };

  const addToCalendar = () =>
    window.open(
      generateCalendarLink(
        {
          appointmentType: type,
          startTime: appointment.startTime,
          endTime: appointment.endTime,
          location: appointment.location
        },
        apptVideoLink
      )
    );

  return (
    <CardContainer $isFuture={isFuture}>
      {confirmation && (
        <>
          <HiddenPatientIdTag patient={patient} />
          <HiddenAppointmentTags appointment={appointment} appointmentType={type} />
        </>
      )}

      {/* For confirmation use-case we want to show name + profile picture if it exists */}
      {displayFullProviderDetails && (
        <ProviderDetailsConfirmation provider={appointment.provider} />
      )}

      <AppointmentDetails
        appointment={appointment}
        appointmentType={type}
        isFuture={isFuture}
        hideTopIcon={displayFullProviderDetails}
        centerAlign={displayFullProviderDetails}
      />

      {/* For non-confirmation use-case we only show provider name */}
      {displayOnlyProviderName && <ProviderDetailsRegular provider={appointment.provider} />}

      <LocationDetails
        appointment={appointment}
        isFuture={isFuture}
        isPhoneCall={isPhoneCall}
        isVideoCall={isVideoCall}
        isInPerson={isInPerson}
        goToVideoLink={goToVideoLink}
      />

      {isFuture && (
        <FutureActionsWrapper>
          <Button
            leftIcon={<AddToCalendarButton />}
            label="Add to Calendar"
            category="secondary"
            size="lg"
            fullWidth
            onClick={addToCalendar}
          />
          {canModifyAppointment && (
            <LinkButton
              text={modifyAppointmentText}
              onClick={toggleCancelModal}
              size="lg"
              underline="none"
            />
          )}
        </FutureActionsWrapper>
      )}

      <ModalCancelReschedule
        visible={showModal}
        onClose={toggleCancelModal}
        onReturnToDashboard={toggleCancelModal}
        appointment={appointment}
        onAppointmentCancelled={onAppointmentCancelled}
        onReschedule={onReschedule}
      />
    </CardContainer>
  );
};
